import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useMedplumProfile } from "@medplum/react";

import { Avatar, Group, Menu, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconBell, IconChevronDown, IconLogout } from "@tabler/icons-react";

import SignOutConfirmation from "@/components/SignOutConfirmation";
import { TextWrapper } from "@/components/TextWrapper";
import { NotificationPreferencesDrawer } from "@/notification-preferences";
import { getDisplayName, getProfilePicture } from "@/utils/person";

export default function UserMenu() {
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const [signOutOpened, { open: openSignOut, close: closeSignOut }] = useDisclosure(false);
  const [
    notificationSettingsOpened,
    { open: openNotificationSettings, close: closeNotificationSettings },
  ] = useDisclosure(false);
  // Overlays open after the menu finishes closing, so the menu's focus return to its trigger
  // does not pull focus out of the overlay's focus trap.
  const openOnMenuCloseRef = useRef<(() => void) | undefined>(undefined);

  const profilePicture = useMemo(() => getProfilePicture(profile), [profile]);
  const displayName = useMemo(() => getDisplayName(profile), [profile]);

  return (
    <>
      <Menu
        shadow="sm"
        width={220}
        position="bottom-end"
        onClose={() => {
          const openOverlay = openOnMenuCloseRef.current;
          openOnMenuCloseRef.current = undefined;
          openOverlay?.();
        }}
      >
        <Menu.Target>
          <UnstyledButton data-testid="user-menu-trigger" aria-label={`${displayName} user menu`}>
            <Group gap="sm">
              <Avatar size="md" radius="xl" variant="light" src={profilePicture} />
              <TextWrapper visibleFrom="sm">{displayName}</TextWrapper>
              <IconChevronDown size={16} aria-hidden />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconBell size={16} />}
            data-testid="user-menu-notification-settings"
            onClick={() => {
              openOnMenuCloseRef.current = openNotificationSettings;
            }}
          >
            Notification Settings
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconLogout size={16} />}
            data-testid="user-menu-sign-out-trigger"
            onClick={() => {
              openOnMenuCloseRef.current = openSignOut;
            }}
          >
            Sign Out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <NotificationPreferencesDrawer
        opened={notificationSettingsOpened}
        onClose={closeNotificationSettings}
      />
      <SignOutConfirmation
        opened={signOutOpened}
        onClose={closeSignOut}
        onSignOut={() => navigate("/logout")}
      />
    </>
  );
}
